import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, listRegistryLaborCos, type RegistryLaborCo, type PackageDetail, type VoteRoundView } from '../lib/api';
import { Modal } from './ui/Modal';

/** Invite labor companies from the supplier database onto this package (creates engagements). Distinct
 *  from "录入报价" — invited suppliers can then log into the portal and submit their own quotes.
 *
 *  `vote` is the package's latest 定标投票 round, read-only here: whom the panel picked AND whom 预算
 *  actually reached (改改 fb_828ed0af). It never blocks — the invite gate is the round's STATUS, checked
 *  on the page behind this modal — it just stops the invite list being picked blind. */
export function InviteContractors({ pkg, onClose, vote }: { pkg: PackageDetail; onClose: () => void; vote?: VoteRoundView | null }) {
  const qc = useQueryClient();
  // refetchOnMount:'always' — opening the modal re-pulls the registry, so a stale/empty cache from an earlier
  // (e.g. pre-login) load never lingers as a misleading "no matches" (改改 6/26).
  const { data: registry, isLoading, isError, refetch } = useQuery<RegistryLaborCo[]>({
    queryKey: ['registry-labor-cos'], queryFn: listRegistryLaborCos, refetchOnMount: 'always', retry: 1,
  });
  const engaged = useMemo(() => new Set(pkg.engagements.filter((e) => !e.withdrawn && e.labor_co_id).map((e) => e.labor_co_id as string)), [pkg.engagements]);
  const [filter, setFilter] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // the vote's own answer to "谁能来": candidates 预算 phoned and confirmed. Purely a highlight.
  // …across EVERY round of the package (the pool), not just the latest one (杨丽刚 fb_712b4d4b)
  const votedAvailable = useMemo(
    () => new Set((vote?.pool?.candidates ?? vote?.candidates ?? []).filter((c) => c.availability === 'available' && c.labor_co_id).map((c) => c.labor_co_id as string)),
    [vote?.pool?.candidates, vote?.candidates],
  );
  const voteShort = vote?.round?.status === 'completed' && (vote.short_by ?? 0) > 0;

  const q = filter.trim().toLowerCase();
  const filtered = (registry ?? []).filter((c) => !q || [c.name, c.internal_code, c.region, c.trades_json].some((v) => (v ?? '').toLowerCase().includes(q)));
  function toggle(id: string) { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); }
  const trades = (j: string | null) => { try { return j ? (JSON.parse(j) as string[]).join('、') : ''; } catch { return ''; } };

  async function invite() {
    const ids = [...sel].filter((id) => !engaged.has(id));
    if (!ids.length) { setMsg('请选择尚未邀请的劳务 / select suppliers not yet invited'); return; }
    setBusy(true); setMsg(null);
    try {
      for (const id of ids) await api(`/packages/${pkg.id}/engagements`, { method: 'POST', body: JSON.stringify({ labor_co_id: id, invite_method: 'manual' }) });
      await qc.invalidateQueries({ queryKey: ['package', pkg.id] });
      setMsg(`已邀请 ${ids.length} 家劳务 / invited ${ids.length}`); setSel(new Set());
    } catch (e) { setMsg(String((e as any)?.message ?? '邀请失败 / invite failed')); }
    finally { setBusy(false); }
  }

  const toInvite = [...sel].filter((id) => !engaged.has(id)).length;
  return (
    <Modal onClose={onClose} title="🤝 邀请劳务 / Invite contractors" width={720}>
        <p className="muted small" style={{ marginTop: 0 }}>从供应商库勾选要邀请的劳务方；被邀请后他们可登录门户、查看招标文件并自行提交报价。/ Pick suppliers from the database; once invited they can log into the portal, see the tender files, and submit their own quotes.</p>
        {voteShort && (
          <div className="card small" style={{ borderColor: 'var(--bad)', marginBottom: 8, padding: '8px 10px' }}>
            ⚠ 定标投票中仍在名单内的候选仅 {vote!.available_count} 家（公开招标需不少于 {vote!.min_bidders} 家）——建议先补充推荐/候选再邀请。
            / Only {vote!.available_count} candidate(s) from the vote are still in play — an open tender needs at least {vote!.min_bidders}. Consider topping the slate up before inviting.
          </div>
        )}
        <input type="text" placeholder="搜索：名称 / 编号 / 工种 / 地区 · search" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ width: '100%', marginBottom: 8 }} />
        <div className="card" style={{ maxHeight: '46vh', overflow: 'auto', padding: 0 }}>
          {filtered.length === 0 && (
            <div className="muted small" style={{ padding: 10 }}>
              {isLoading ? '加载供应商库中… / loading…'
                : isError ? <>⚠ 供应商库加载失败，请刷新页面或重新登录。/ Failed to load — refresh or re-login. <button className="small" style={{ marginLeft: 6 }} onClick={() => refetch()}>重试 / retry</button></>
                : (registry?.length ?? 0) === 0 ? '供应商库为空 — 请先在「供应商库」导入劳务。/ Registry is empty — import suppliers first.'
                : '无匹配 / no matches'}
            </div>
          )}
          {filtered.map((c) => {
            const isEngaged = engaged.has(c.id);
            return (
              <label key={c.id} className="row-wrap" style={{ gap: 8, alignItems: 'baseline', padding: '5px 10px', borderBottom: '1px solid var(--rule)', cursor: isEngaged ? 'default' : 'pointer', opacity: isEngaged ? 0.55 : 1 }}>
                <input type="checkbox" checked={isEngaged || sel.has(c.id)} disabled={isEngaged} onChange={() => toggle(c.id)} />
                {c.internal_code && <span className="pill small pill-muted">{c.internal_code}</span>}
                <strong>{c.name}</strong>
                <span className="muted small" style={{ flex: 1 }}>{trades(c.trades_json)}{c.region ? ` · ${c.region}` : ''}</span>
                {c.status !== 'active' && <span className="pill small pill-warn">{c.status}</span>}
                {votedAvailable.has(c.id) && (
                  <span className="pill small pill-good" title="定标投票的候选，且已联系确认可参与 / a vote candidate 预算 phoned and confirmed">投票可参与 / available</span>
                )}
                {isEngaged && <span className="pill small pill-good">已邀请 / invited</span>}
              </label>
            );
          })}
        </div>
        <div className="row" style={{ marginTop: 12, alignItems: 'center' }}>
          {msg && <span className="small" style={{ color: 'var(--good)' }}>{msg}</span>}
          <span className="spacer" style={{ flex: 1 }} />
          <button className="btn-primary" disabled={busy || toInvite === 0} onClick={invite}>{busy ? '邀请中…' : `邀请所选 (${toInvite}) / Invite`}</button>
        </div>
    </Modal>
  );
}
